import type { Assessment } from "../assessment-types";

/** 🌊 Emotions — phase 1 expansion. */

export const EMOTIONAL_FLASHBACKS_ASSESSMENT: Assessment = {
  slug: "emotional-flashbacks",
  emoji: "⚡",
  title: "Emotional Flashbacks",
  tagline: "When emotions from the past feel present again",
  category: "emotions",
  keywords: ["flashback", "emotional flashback", "past", "sudden", "regression", "overwhelm"],
  intro:
    "This reflects on moments when a feeling arrives with an intensity that seems to belong to another time. Many people experience this without any visual memory at all.",
  questions: [
    { id: "q1", text: "Do strong feelings sometimes arrive suddenly without a clear cause?" },
    { id: "q2", text: "Do you ever feel much younger or smaller than you are during difficult moments?" },
    { id: "q3", text: "Do feelings of fear, shame or hopelessness appear very quickly?" },
    { id: "q4", text: "Do small events sometimes bring a wave of emotion that feels enormous?" },
    { id: "q5", text: "Do you find it hard to think clearly while the feeling is happening?" },
    { id: "q6", text: "Does it take a long time to come back to yourself afterwards?" },
    { id: "q7", text: "Do you feel embarrassed or confused by the intensity later?" },
    { id: "q8", text: "Do certain relationships or situations bring these waves more than others?" },
    { id: "q9", text: "Do you notice your body reacting strongly at the same time?" },
    { id: "q10", text: "Do you sometimes feel convinced things will never change while it's happening?" },
    { id: "q11", text: "Do these moments pass and then feel unreal afterwards?" },
    { id: "q12", text: "Would it help you to recognise these waves earlier?" },
  ],
  bands: [
    {
      key: "low",
      minPercent: 0,
      headline: "Your answers suggest these emotional waves are not a strong pattern right now.",
      body:
        "Most people have moments where a feeling arrives out of proportion. Recognising the early signs is useful even when they're rare.",
    },
    {
      key: "some",
      minPercent: 30,
      headline: "Your responses suggest you may recognise some emotional flashbacks.",
      body:
        "These waves happen when feeling-memory activates without a clear picture attached. Naming what is happening — 'this is old feeling, arriving now' — often reduces the intensity.",
    },
    {
      key: "often",
      minPercent: 55,
      headline: "Your responses suggest emotional flashbacks may be affecting you fairly often.",
      body:
        "They can be disorienting, especially when there is no obvious trigger. Grounding in the present moment and knowing your own signals can help you ride them more safely.",
    },
    {
      key: "high",
      minPercent: 78,
      headline: "Your responses suggest emotional flashbacks may be a frequent and heavy experience.",
      body:
        "That takes a great deal out of you. Working with someone appropriately qualified, alongside gentle daily grounding, is often helpful with this pattern. If you ever feel unsafe, please reach out to crisis support.",
    },
  ],
  explore: {
    understand: [
      { label: "Emotional flashbacks explained", to: "/learn/emotional-flashbacks" },
      { label: "Complex trauma", to: "/learn/cptsd" },
    ],
    try: [
      { label: "Grounding exercises", to: "/grounding" },
      { label: "Bilateral exercises", to: "/bilateral" },
    ],
    reflect: [
      { label: "Inner child reflections", to: "/inner-child" },
      { label: "Trigger Journal", to: "/trigger-journal" },
    ],
  },
};

export const ANXIETY_PATTERNS_ASSESSMENT: Assessment = {
  slug: "anxiety-patterns",
  emoji: "🌪️",
  title: "Anxiety Patterns",
  tagline: "How worry, fear and physical activation show up",
  category: "emotions",
  keywords: ["anxiety", "worry", "panic", "nervous", "fear", "racing heart", "dread"],
  intro:
    "This reflects on the way worry and physical activation appear in your days. It looks at patterns you may notice, not at any condition.",
  questions: [
    { id: "q1", text: "Do you notice worry running in the background of your day?" },
    { id: "q2", text: "Does your body feel tense, restless or wired?" },
    { id: "q3", text: "Do you notice your heart racing or your breathing changing?" },
    { id: "q4", text: "Do you find it hard to switch off at the end of the day?" },
    { id: "q5", text: "Do you expect something to go wrong?" },
    { id: "q6", text: "Do you seek reassurance from others about the same worries?" },
    { id: "q7", text: "Do you avoid situations because of how anxious they make you feel?" },
    { id: "q8", text: "Do you rehearse conversations before they happen?" },
    { id: "q9", text: "Does your stomach or chest react when you feel uneasy?" },
    { id: "q10", text: "Do you feel uneasy without being able to name why?" },
    { id: "q11", text: "Do you find it hard to concentrate when worry is loud?" },
    { id: "q12", text: "Do you feel tired from how much energy worry takes?" },
  ],
  bands: [
    {
      key: "low",
      minPercent: 0,
      headline: "Your answers suggest anxious activation is fairly quiet for you at the moment.",
      body:
        "Some worry is a normal part of being human. Noticing what keeps your system settled is worth as much attention as noticing what unsettles it.",
    },
    {
      key: "some",
      minPercent: 30,
      headline: "Your responses suggest anxious patterns show up for you some of the time.",
      body:
        "Anxiety is often the body preparing for something. Slower breathing and orienting to your surroundings can gently signal that this moment is different.",
    },
    {
      key: "often",
      minPercent: 55,
      headline: "Your responses suggest worry and physical activation may be present quite often.",
      body:
        "Living with this takes real energy. Regular, small regulation practices tend to help more than trying to think your way out of the feeling.",
    },
    {
      key: "high",
      minPercent: 78,
      headline: "Your responses suggest anxious patterns may be affecting you a great deal.",
      body:
        "That is heavy to carry day to day. Considering support from an appropriately qualified professional alongside daily regulation may be worthwhile. If you feel unsafe at any point, please use crisis support.",
    },
  ],
  explore: {
    understand: [
      { label: "Hypervigilance explained", to: "/learn/hypervigilance" },
      { label: "The window of tolerance", to: "/learn/window-of-tolerance" },
    ],
    try: [
      { label: "Breathing exercises", to: "/breathing" },
      { label: "EFT tapping", to: "/tapping" },
    ],
    reflect: [
      { label: "CBT thought cards", to: "/cbt-thought-cards" },
      { label: "Guided journal", to: "/guided-journal" },
    ],
  },
};

export const SELF_COMPASSION_ASSESSMENT: Assessment = {
  slug: "self-compassion",
  emoji: "💛",
  title: "Self-Compassion",
  tagline: "How do you respond to yourself when you're struggling?",
  category: "emotions",
  keywords: ["self-compassion", "kindness", "gentle", "self-care", "harsh", "forgiveness"],
  intro:
    "This reflects on the way you meet yourself in difficult moments. Self-compassion is a skill that can be learned, not a personality trait you either have or don't.",
  questions: [
    { id: "q1", text: "Do you speak to yourself harshly when something goes wrong?" },
    { id: "q2", text: "Do you find it hard to give yourself the kindness you'd give a friend?" },
    { id: "q3", text: "Do you feel you have to earn rest or care?" },
    { id: "q4", text: "Do you dismiss your own struggles as not bad enough to matter?" },
    { id: "q5", text: "Do you feel uncomfortable when someone is gentle with you?" },
    { id: "q6", text: "Do you keep going instead of acknowledging that something was hard?" },
    { id: "q7", text: "Do you compare your progress unfavourably with other people's?" },
    { id: "q8", text: "Do you find it difficult to forgive yourself for past decisions?" },
    { id: "q9", text: "Do you notice guilt when you look after yourself?" },
    { id: "q10", text: "Do you expect more of yourself than you would of anyone else?" },
    { id: "q11", text: "Do you struggle to name what you need?" },
    { id: "q12", text: "Would you like the way you speak to yourself to feel kinder?" },
  ],
  bands: [
    {
      key: "low",
      minPercent: 0,
      headline: "Your answers suggest you can often meet yourself with some warmth.",
      body:
        "That is a genuine resource. Keeping it strong on hard days matters more than having it on easy ones.",
    },
    {
      key: "some",
      minPercent: 30,
      headline: "Your responses suggest self-compassion is sometimes harder to reach.",
      body:
        "Many people learn early that kindness must be earned. Practising one softer sentence towards yourself is a small, real place to start.",
    },
    {
      key: "often",
      minPercent: 55,
      headline: "Your responses suggest being gentle with yourself may often feel difficult.",
      body:
        "This usually develops when care was inconsistent or conditional. Self-compassion tends to grow slowly, through repetition rather than insight alone.",
    },
    {
      key: "high",
      minPercent: 78,
      headline: "Your responses suggest self-kindness may feel very hard to access right now.",
      body:
        "That is painful, and it makes sense given what you may have lived through. You don't have to feel compassion to begin practising it — starting with neutrality is enough.",
    },
  ],
  explore: {
    understand: [
      { label: "Shame-based trauma", to: "/learn/shame-based-trauma" },
      { label: "The inner critic", to: "/learn/inner-critic" },
    ],
    try: [
      { label: "Self-love practices", to: "/self-love" },
      { label: "Affirmations", to: "/affirmations" },
    ],
    reflect: [
      { label: "Letters never sent", to: "/integrate/letters-never-sent" },
      { label: "Inner child reflections", to: "/inner-child" },
    ],
  },
};
